import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { getUserData } from "../../utils/authUtils";
import type { RootState } from "../store";

export interface InvoiceItem {
  id: string;
  invoice_no: string;
  payment_mode: string;
  invoice_date: string;
  invoice_date_str?: string;
  invoice_link: string;
}

interface InvoicesState {
  items: InvoiceItem[];
  loading: boolean;
  error: string | null;
  loaded: boolean;          // ✅ prevent duplicate loads
  lastFetchedAt?: number;   // optional, useful for TTL
}

const initialState: InvoicesState = {
  items: [],
  loading: false,
  error: null,
  loaded: false,
};

export const fetchCustomerInvoices = createAsyncThunk<
  InvoiceItem[],
  { force?: boolean } | void,
  { state: RootState; rejectValue: string }
>(
  "invoices/fetchCustomerInvoices",
  async (_arg, { rejectWithValue }) => {
    try {
      const u = getUserData() || {};
      const customerId =
        u?.Customerdetail?.id || u?.customer_id || u?.id || "";

      if (!customerId) {
        return rejectWithValue("Missing customer_id. Please login again.");
      }

      const formData = new FormData();
      formData.append("customer_id", String(customerId));

      const response = await axios.post(
        `${API_BASE_URL}/get_invoice_list_by_customer`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data?.status !== "success") {
        return rejectWithValue(
          response.data?.message || "Failed to fetch invoices."
        );
      }

      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      return data.map((d: any) => ({
        id: String(d?.id ?? ""),
        invoice_no: String(d?.invoice_no ?? ""),
        payment_mode: String(d?.payment_mode ?? ""),
        invoice_date: String(d?.invoice_date ?? ""),
        invoice_date_str: d?.invoice_date_str
          ? String(d.invoice_date_str)
          : undefined,
        invoice_link: String(d?.invoice_link ?? "#"),
      }));
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Failed to fetch invoices."
      );
    }
  },
  {
    // ✅ Runs before the thunk body: cancel if already loaded or loading
    condition: (arg, { getState }) => {
      const state = getState() as RootState;
      const { loading, loaded } = state.invoices;

      const force = !!(arg as any)?.force;
      if (force) return true;     // allow manual refresh

      if (loading) return false;  // already in-flight
      if (loaded) return false;   // already fetched once

      return true;
    },
  }
);

const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    clearInvoices(state) {
      state.items = [];
      state.loading = false;
      state.error = null;
      state.loaded = false; // allow re-fetch after clearing
      state.lastFetchedAt = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomerInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.loaded = true;               // ✅ mark as loaded
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchCustomerInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch invoices.";
      });
  },
});

export const { clearInvoices } = invoicesSlice.actions;
export default invoicesSlice.reducer;
